import { datadogRum } from '@datadog/browser-rum-slim';

import { routes } from '../routes';

interface SessionTimerOptions {
  /** The interval in seconds at which the session timer checks for user activity. */
  checkIntervalSeconds: number;
  /** The duration in seconds for the session to last. */
  sessionDurationSeconds: number;
}

/**
 * The SessionTimer class is responsible for managing a user's session.
 * It keeps track of the user's last activity and determines when the session should be refreshed or expired.
 *
 * Note: This class is a singleton and can only be instantiated once.
 */
class SessionTimer {
  private sessionDurationMs: number;
  private checkIntervalMs: number;
  private lastActivityType: 'scroll' | 'mouse-movement' | 'keyboard' | 'iframe-interaction' | 'init';
  private lastActivityDateTime: number;
  private expiresAt: number;
  private interval: NodeJS.Timeout;

  constructor({ checkIntervalSeconds, sessionDurationSeconds }: SessionTimerOptions) {
    this.lastActivityType = 'init';
    this.sessionDurationMs = sessionDurationSeconds * 1000;
    this.checkIntervalMs = checkIntervalSeconds * 1000;
    this.lastActivityDateTime = Date.now();
    this.expiresAt = Date.now() + this.sessionDurationMs;
    this.interval = setInterval(this.handleInterval, this.checkIntervalMs);
  }

  userActivity = (listenerType: 'scroll' | 'mouse-movement' | 'keyboard' | 'iframe-interaction') => {
    this.lastActivityDateTime = Date.now();
    this.lastActivityType = listenerType;
  };

  private refreshSession = async () => {
    const currentActivity = this.lastActivityDateTime;
    const currentExpiry = this.expiresAt;

    try {
      const response = await fetch('/api/baq', {
        mode: 'no-cors',
      });

      if (response.ok) {
        const timeSinceLastActivitySeconds = ((Date.now() - currentActivity) / 1000).toFixed(0);
        const humanFriendlyDiff = `Refreshing session due to recent (${this.lastActivityType}) activity (${timeSinceLastActivitySeconds} seconds ago)`;
        this.expiresAt = Date.now() + this.sessionDurationMs;

        datadogRum.addAction('session_refreshed', {
          activity: new Date(currentActivity).toISOString(),
          expiry: new Date(currentExpiry).toISOString(),
          message: humanFriendlyDiff,
        });
      } else {
        // Don't log the user out on error of the refresh endpoint. The error could be due to, for example, a blip in the SECCL auth API. No need to destroy the session as a result.
        // Just log it instead.
        datadogRum.addAction('session_refresh_failed');
      }
    } catch (e) {
      // Don't log out in the case of a network error, just wait for the next session check.
      // Once the session expires the user will be logged out regardless.

      if (!(e instanceof Error) && (typeof e === 'string' || e === undefined)) {
        // eslint-disable-next-line no-ex-assign
        e = new Error(e);
      }
      if (e instanceof Error) {
        datadogRum.addAction('Network error attempting to invoke the session refresh endpoint', {
          activity: new Date(currentActivity).toISOString(),
          expiry: new Date(currentExpiry).toISOString(),
          message: e.message,
        });
      }
    }
  };

  private handleInterval = async () => {
    const now = Date.now();

    // If there has been activity in the last activity window (ignoring initialisation), refresh the session
    if (this.lastActivityType !== 'init' && this.lastActivityDateTime > now - this.checkIntervalMs) {
      return await this.refreshSession();
    }

    // If the session has expired, log the user out
    if (now > this.expiresAt) {
      return await this.destroy('Session has expired');
    }
  };

  public destroy = async (reason: string) => {
    datadogRum.addAction('session_expired', {
      message: reason,
    });

    clearInterval(this.interval);

    window.location.href = routes.auth.auth0.logout;
  };
}

let instance: SessionTimer;

export const getSessionTimer = (args: SessionTimerOptions): SessionTimer => {
  if (!instance) {
    instance = new SessionTimer(args);
  }
  return instance;
};
